import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { MY_RECIPES as MR } from '../constants/strings';
import { formatTime } from '../utils/time';
import { getKiLabel, getKiTooltip } from '../utils/recipe';
import { IconClose, IconWand } from '../components/icons';
import './MyRecipes.css';

const WARM_CATS  = ['Hauptgerichte','Fleisch & Geflügel','Geflügel','Grill & BBQ','Aus dem Ofen','Aufläufe & Gratins'];
const COOL_CATS  = ['Suppen & Eintöpfe','Fisch & Meeresfrüchte','Pasta & Nudeln','Reis & Getreide'];
const GREEN_CATS = ['Beilagen','Vegetarisch','Vegan','Salate','Gemüse & Beilagen','Hülsenfrüchte'];
const SWEET_CATS = ['Desserts & Süßes','Frühstück & Brunch','Snacks & Fingerfood','Pizza & Flammkuchen'];

function _getPlaceholderColor(recipe) {
  const cat = recipe.category || '';
  if (cat.includes('Dessert') || cat.includes('Süß')) return 'amber';
  if (cat.includes('Vegetar') || cat.includes('Vegan') || cat.includes('Salat')) return 'green';
  if (cat.includes('Suppe') || cat.includes('Fisch')) return 'blue';
  if (cat.includes('Fleisch') || cat.includes('Grill')) return 'red';
  const charCode = (recipe.title || 'A').charCodeAt(0);
  const colors = ['green', 'amber', 'blue', 'red', 'purple'];
  return colors[charCode % colors.length];
}

function catColorClass(cat) {
  if (!cat) return 'cat-neutral';
  if (WARM_CATS.includes(cat))  return 'cat-warm';
  if (COOL_CATS.includes(cat))  return 'cat-cool';
  if (GREEN_CATS.includes(cat)) return 'cat-green';
  if (SWEET_CATS.includes(cat)) return 'cat-sweet';
  return 'cat-neutral';
}


function MyRecipes() {
  const { user } = useAuth();
  const [recipes, setRecipes]           = useState([]);
  const [loading, setLoading]           = useState(true);
  const [search, setSearch]             = useState('');
  const [activeCategory, setActiveCategory] = useState('Alle');
  const [sort, setSort]                 = useState('newest');
  const [deletingId, setDeletingId]     = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleteError, setDeleteError]   = useState('');
  const [openMenuId, setOpenMenuId]     = useState(null);

  useEffect(() => { loadMyRecipes(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    function handleClickOutside() { setOpenMenuId(null); }
    if (openMenuId) document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [openMenuId]);

  async function loadMyRecipes() {
    try {
      const data = await api.getRecipes({ limit: 500 });
      const all = data.recipes || [];
      setRecipes(all.filter(r => r.user_id === user.id));
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  async function handleDelete(id) {
    setDeleteError('');
    setDeletingId(id);
    setConfirmDelete(null);
    try {
      await api.deleteRecipe(id);
      setRecipes(prev => prev.filter(r => r.id !== id));
    } catch (err) {
      setDeleteError('Löschen fehlgeschlagen: ' + err.message);
    } finally {
      setDeletingId(null);
    }
  }

  const CATEGORY_ALIASES = {
    'Geflügel': 'Fleisch & Geflügel',
  };

  const categories = ['Alle', ...Array.from(new Set(
    recipes.map(r => CATEGORY_ALIASES[r.category] || r.category).filter(Boolean)
  )).sort()];

  const filtered = recipes
    .filter(r => {
      const matchSearch = !search ||
        r.title.toLowerCase().includes(search.toLowerCase()) ||
        (r.category || '').toLowerCase().includes(search.toLowerCase());
      const normalizedCat = CATEGORY_ALIASES[r.category] || r.category;
      const matchCat = activeCategory === 'Alle' || normalizedCat === activeCategory;
      return matchSearch && matchCat;
    })
    .sort((a, b) => {
      if (sort === 'newest') return new Date(b.created_at) - new Date(a.created_at);
      if (sort === 'oldest') return new Date(a.created_at) - new Date(b.created_at);
      if (sort === 'az') return a.title.localeCompare(b.title, 'de');
      return 0;
    });

  if (loading) {
    return (
      <div className="my-recipes-page">
        <div className="loading-state">
          <div className="loader" />
          <p>{MR.loading}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="my-recipes-page fade-in">

      {/* ── Header ── */}
      <div className="page-header">
        <div className="page-header-left">
          <div className="page-title">{MR.title}</div>
          <div className="page-sub">
            {MR.subtitle(recipes.length)}
          </div>
        </div>
        <Link to="/upload" className="btn btn-primary">
          <svg viewBox="0 0 24 24"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          Neues Rezept
        </Link>
      </div>

      {recipes.length === 0 ? (
        <div className="mr-empty">
          <div className="mr-empty-icon">📷</div>
          <h3>{MR.emptyTitle}</h3>
          <p>{MR.emptyText}</p>
          <Link to="/upload" className="btn btn-primary">{MR.emptyBtn}</Link>
        </div>
      ) : (
        <>
          {/* ── Filter-Leiste ── */}
          <div className="filter-bar">
            <div className="filter-search">
              <svg viewBox="0 0 24 24"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
              <input
                type="text"
                placeholder="In meinen Rezepten suchen …"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
            <select
              value={sort}
              onChange={e => setSort(e.target.value)}
              className="sort-select"
            >
              <option value="newest">Neueste zuerst</option>
              <option value="oldest">Älteste zuerst</option>
              <option value="az">A – Z</option>
            </select>
          </div>

          {/* ── Kategorie-Pills ── */}
          {categories.length > 2 && (
            <div className="mr-filter-row">
              <div className="mr-cat-scroll">
                {categories.map(cat => (
                  <button
                    key={cat}
                    className={`cf-pill${activeCategory === cat ? ' active' : ''}`}
                    onClick={() => setActiveCategory(cat)}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* ── Filter-Info ── */}
          {(search || activeCategory !== 'Alle') && (
            <p className="mr-filter-info">
              {filtered.length} Treffer
              {activeCategory !== 'Alle' && ` in „${activeCategory}"`}
              {search && ` für „${search}"`}
              <button className="mr-filter-clear" onClick={() => { setSearch(''); setActiveCategory('Alle'); }}>
                <IconClose size={12} /> Filter zurücksetzen
              </button>
            </p>
          )}

          {deleteError && <div className="mr-error">{deleteError}</div>}

          {/* ── Rezept-Zeilen ── */}
          {filtered.length === 0 ? (
            <div className="mr-empty"><p>{MR.filterEmpty}</p></div>
          ) : (
            <div className="mr-grid">
              {filtered.map(recipe => (
                <div key={recipe.id} className="mr-card">

                  {getKiLabel(recipe.content_source) && (
                    <span
                      className="ki-badge-thumb"
                      title={getKiTooltip(recipe.content_source)}
                      style={{ position: 'absolute', bottom: 8, right: 8, zIndex: 2 }}
                    >
                      <IconWand size={11} /> {getKiLabel(recipe.content_source)}
                    </span>
                  )}

                  {/* Bild */}
                  <Link to={`/recipe/${recipe.id}`} className="mr-card-img-wrap">
                    {recipe.image_path && !recipe.image_path.includes('/uploads/users/') ? (
                      <img src={recipe.image_path} alt={recipe.title} className="mr-card-img" />
                    ) : (
                      <div className={`mr-card-placeholder mr-card-placeholder--${_getPlaceholderColor(recipe)}`}>
                        <span className="mr-placeholder-initial">{(recipe.title || '?')[0].toUpperCase()}</span>
                      </div>
                    )}
                  </Link>

                  {/* Body */}
                  <div className="mr-card-body">
                    <Link to={`/recipe/${recipe.id}`} className="mr-card-title">
                      {recipe.title}
                    </Link>
                    <div className="mr-card-meta">
                      {recipe.category && (
                        <span className={`cat-badge ${catColorClass(recipe.category)}`}>
                          {recipe.category}
                        </span>
                      )}
                      {recipe.prep_time > 0 && (
                        <span className="rr-meta">
                          <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                          {formatTime(recipe.prep_time)}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Drei-Punkte-Menü */}
                  <div className="mr-card-menu" onClick={e => e.stopPropagation()}>
                    <button
                      className="mr-menu-btn"
                      onClick={() => setOpenMenuId(openMenuId === recipe.id ? null : recipe.id)}
                      title="Optionen"
                    >
                      ⋮
                    </button>
                    {openMenuId === recipe.id && (
                      <div className="mr-menu-dropdown">
                        <Link
                          to={`/recipe/${recipe.id}`}
                          className="mr-menu-item"
                          onClick={() => setOpenMenuId(null)}
                        >
                          Ansehen
                        </Link>
                        <Link
                          to={`/edit/${recipe.id}`}
                          className="mr-menu-item"
                          onClick={() => setOpenMenuId(null)}
                        >
                          Bearbeiten
                        </Link>
                        {confirmDelete === recipe.id ? (
                          <div className="mr-menu-confirm">
                            <span>Wirklich löschen?</span>
                            <div className="mr-menu-confirm-btns">
                              <button
                                className="mr-menu-confirm-yes"
                                onClick={() => { handleDelete(recipe.id); setOpenMenuId(null); }}
                              >
                                Ja, löschen
                              </button>
                              <button
                                className="mr-menu-confirm-no"
                                onClick={() => { setConfirmDelete(null); setOpenMenuId(null); }}
                              >
                                Abbrechen
                              </button>
                            </div>
                          </div>
                        ) : (
                          <button
                            className="mr-menu-item mr-menu-item--danger"
                            onClick={() => { setDeleteError(''); setConfirmDelete(recipe.id); }}
                            disabled={deletingId === recipe.id}
                          >
                            {deletingId === recipe.id ? '…' : 'Löschen'}
                          </button>
                        )}
                      </div>
                    )}
                  </div>

                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default MyRecipes;
